import { useRemote } from '../../../Utils/Utils';
import { useEffect, useContext, useMemo, useState } from 'react';
import { displayValueFunction, analyticsExportFilename } from '../AnalyticsUtils';
import { BannerContext } from '../../../InternalApiApp';
import AnalyticsLineChart from '../Charts/AnalyticsLineChart';
import LoadingIcon from '../../../components/LoadingIcon';
import AnalyticsHeader from './AnalyticsHeader';
import moment from 'moment';


const LineChartWrapper = ({ label, endpoint, guidelines, tooltipGuidelines, barKey1, barKey2, dy, legends, legendUnit, tickWidth, axisLabelClass, mb, passedTimeFrame, customeDate }) => {
    const remote = useRemote();
    const { bannerAdditionalInfo } = useContext(BannerContext)
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [timeFrame, setTimeFrame] = useState(passedTimeFrame);
    const [rawData, setRowData] = useState(null)

    useEffect(() => {
        if (bannerAdditionalInfo && customeDate && customeDate.from && customeDate.to) {
            setLoading(true);
            const end = moment(customeDate.to).format('YYYY-MM-DD');
            const start = moment(customeDate.from).format('YYYY-MM-DD');
            const products = bannerAdditionalInfo?.platforms.map(platform =>
                platform.products.map(p => p.selected ? `product=${p.id}` : null)
                    .filter(e => e !== null)
                    .join("&")
            )
            const path = endpoint.replace("${end}", end).replace("${date}", start).replace("${products}", products ? "&" + products : "")

            remote.get(path)
                .then((response) => {
                    const isArrayResponse = Array.isArray(response);

                    const list = [];
                    if (isArrayResponse) {
                        for (var i = 0; i < response.length; i++) {
                            const newObj = {}
                            guidelines.forEach(g => {
                                const obj = displayValueFunction(response[i], g);
                                if (!newObj[g.keyName]) {
                                    newObj[g.keyName] = obj[g.keyName];
                                }
                            });
                            list.push(newObj);
                        }
                    } else if (typeof response === 'object' && response !== null) {
                        const arr = Object.entries(response);
                        arr.forEach(([key, value]) => {
                            const newObj = {};
                            guidelines.forEach((g, index) => {
                                if (index === 0) {
                                    newObj[g.keyName] = key;
                                } else {
                                    if (typeof value === "number") {
                                        newObj[g.keyName] = value;
                                    } else {
                                        newObj[g.keyName] = value[g.column];
                                    }
                                }
                            });
                            list.push(newObj);
                        });
                    }
                    setData(list);
                    setRowData(response)
                    setLoading(false);

                }).catch((e) => {
                    console.error("Error fetching unique portfolios per session data:", e);
                    setLoading(false);
                });
        }
    }, [bannerAdditionalInfo, timeFrame, passedTimeFrame, customeDate, remote])


    function renderTooltip(payload) {
        if (!payload || !payload.active || !payload.payload || payload.payload.length === 0) return null;
        let name = payload.payload[0].payload.name;
        const formattedName = tooltipGuidelines && tooltipGuidelines[0].keyName && tooltipGuidelines[0].keyName === "name" ? displayValueFunction({ [tooltipGuidelines[0].keyName]: name }, tooltipGuidelines[0]) : name
        let value = payload.payload[0].payload[barKey1];
        const formattedValue = tooltipGuidelines && tooltipGuidelines[1].keyName && tooltipGuidelines[1].keyName === barKey1 ? displayValueFunction({ [tooltipGuidelines[1].keyName]: value }, tooltipGuidelines[1]) : value
        let value2 = barKey2 ? payload.payload[0].payload[barKey2] : null;
        const formattedValue2 = barKey2 && tooltipGuidelines && tooltipGuidelines[2].keyName && tooltipGuidelines[2].keyName === barKey2 ? displayValueFunction({ [tooltipGuidelines[2].keyName]: value2 }, tooltipGuidelines[2]) : value2

        return (
            <div style={{ padding: 10, borderRadius: '0.25rem', boxShadow: '0 1px 5px rgba(0, 0, 0, 0.15)', display: 'flex', gap: '0.5rem', flexDirection: 'column', backgroundColor: '#fff' }}>
                <div type="body" style={{ fontSize: '1rem', color: '#333B5A' }}>{formattedName}</div>
                <div type="body" style={{ display: "flex", alignItems: "center", gap: 8, color: "#0E8EFD", fontWeight: 500, fontSize: '1.25rem' }}>
                    {tooltipGuidelines[1] && tooltipGuidelines[1].key || ""}{formattedValue}
                </div>
                {value2 &&
                    <div type="body" style={{ display: "flex", alignItems: "center", gap: 8, color: "#62d9b7", fontWeight: 500, fontSize: '1.25rem' }}>
                        {tooltipGuidelines[2] && tooltipGuidelines[2].key || ""}{formattedValue2}
                    </div>
                }
            </div>
        );
    }

    function tickFormatter(value) {
        if (guidelines && guidelines[0]) {
            return displayValueFunction({ [guidelines[0].keyName]: value }, guidelines[0]);
        } else return value;
    }

    function yTickFormatter(value) {
        if (guidelines && guidelines[1]) {
            return displayValueFunction({ [guidelines[1].keyName]: value }, guidelines[1]);
        } else return value;
    }


    const exportHeaders = useMemo(() => [
        { label: 'Date', key: 'name' },
        { label: 'Avg Session Duration (s)', key: 'avgDuration' },
        { label: 'Total Session Duration (s)', key: 'totalDuration' },
    ], []);

    const exportFilename = useMemo(
        () => analyticsExportFilename('avg-session-duration', customeDate),
        [customeDate]
    );

    return (
        <>
            <AnalyticsHeader
                timeFrame={customeDate ? passedTimeFrame : timeFrame}
                setTimeFrame={setTimeFrame}
                mb={mb}
                exportHeaders={exportHeaders}
                exportData={data}
                exportFilename={exportFilename}
                exportLoading={loading}
            >{label ? label : "Search Terms"}</AnalyticsHeader>
            {loading ?
                <div style={{ height: 274, display: 'flex' }}>
                    <LoadingIcon centered />
                </div>
                : rawData?.length === 0
                    ? <div style={{ height: 274 }}>
                        <p className='colour-text-neutral-dark-50'>No data to display</p>
                    </div>
                    : <AnalyticsLineChart
                        data={data}
                        key1={barKey1}
                        key2={barKey2}
                        renderTooltip={renderTooltip}
                        tickFormatter={tickFormatter}
                        yTickFormatter={yTickFormatter}
                        dy={dy}
                        legendUnit={legendUnit}
                        legends={legends}
                        tickWidth={tickWidth}
                        yTicksGap={30}
                        axisLabelClass={axisLabelClass}
                    />
            }
        </>
    );
}

export default LineChartWrapper;